import React, { useCallback, useMemo } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import DownloadFileFormComponent from 'system/components/batch-management/file-managment/download/list';
import { BatchDownloadFileCrumbs } from '../../constants';
import { IconButton, Typography, useEventCallback } from '@mui/material';
import { useFormik } from 'formik';
import { initialValuesBatchDownloadFile } from '..';
import { headCells, validationBatchDownloadFile } from './constants';
import { BatchDownloadFileFilters, BatchDownloadFileManagment } from './types';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { BatchFileAndDirectory } from 'shell/models/batch-file-managment';
import { Download } from '@mui/icons-material';
import { downloadBlob } from 'front-office/utils/download-blob';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { DateTime } from 'luxon';
import { getJobName } from '../../upload/utils';

const GestioneBatchDownloadFile = (): React.ReactElement => {
  const [filesAndDirectories, getFilesAndDirectories] = useHttpFetch<
    Array<BatchFileAndDirectory>,
    Array<BatchDownloadFileManagment>
  >(`${__PRACTICE_API_URL__}/pratiche/gestione-file-nfs/list`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
    },

    transformer: (response) => {
      const data = response?.data || [];
      const itemFiletered = data?.filter((item) => item?.tipoRisorsa !== 'DIR') || [];
      return itemFiletered?.map((item) => {
        const itemResult: BatchDownloadFileManagment = {
          code: item?.name || '',
          context: item?.pathFunzione || '',
          description: item?.tipoRisorsa,
          updateDate: item?.dataAggiornamento
            ? DateTime.fromISO(item?.dataAggiornamento).toFormat('dd/MM/yyyy HH:mm:ss')
            : '',
        };
        return itemResult;
      });
    },
  });

  const getDownloadFile = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/pratiche/gestione-file-nfs/download`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
      },
    }
  );

  const onSubmit = useEventCallback((formValues: BatchDownloadFileFilters) => {
    const jobName = getJobName(formValues?.batchName, formValues?.batchDescription);
    getFilesAndDirectories({
      data: {
        jobName,
        nmCartellaOutput: formValues?.folderName,
      },
    }) as ResponsePromise<Array<BatchFileAndDirectory>, Array<BatchDownloadFileManagment>>;
  });

  const downloadFileForm = useFormik<BatchDownloadFileFilters>({
    initialValues: initialValuesBatchDownloadFile,
    validationSchema: validationBatchDownloadFile,
    onSubmit,
  });

  const handleChangePage = useCallback(
    (newPage) => {
      downloadFileForm?.setFieldValue('page', newPage);
    },
    [downloadFileForm.setFieldValue, downloadFileForm?.values]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...downloadFileForm.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      downloadFileForm.setValues(newDataset);
    },
    [downloadFileForm.setFieldValue, downloadFileForm?.values]
  );

  const onDownload = useEventCallback((item: BatchDownloadFileManagment) => {
    (
      getDownloadFile({
        data: {
          pathFunzione: item?.context,
          fileName: item?.code,
        },
        responseType: 'blob',
      }) as ResponsePromise<Blob>
    ).then((response) => {
      if (response?.data) {
        downloadBlob(response?.data, item?.code);
      }
    });
  });

  const body: Array<BatchDownloadFileManagment> = useMemo(() => {
    return (
      (filesAndDirectories?.data as Array<BatchDownloadFileManagment>)?.map((item) => ({
        ...(item || {}),
        action: (
          <>
            <IconButton onClick={() => onDownload(item)}>
              <Download />
            </IconButton>
          </>
        ),
      })) || []
    );
  }, [filesAndDirectories?.data, onDownload]);

  const count: number = useMemo(() => {
    return body?.length || 0;
  }, [body]);
  return (
    <>
      <Breadcrumbs items={BatchDownloadFileCrumbs} />
      <div className="pmi-my-2">
        <Typography variant="h5">Download</Typography>
      </div>
      <div className="pmi-my-3">
        <DownloadFileFormComponent downloadFileForm={downloadFileForm} />
      </div>
      {filesAndDirectories?.data ? (
        (filesAndDirectories?.data as Array<BatchDownloadFileManagment>)?.length > 0 ? (
          <Table
            onSort={noop}
            head={headCells}
            orders={downloadFileForm.values.orders}
            page={downloadFileForm.values.page}
            rowsPerPage={downloadFileForm.values.rowsPerPage}
            body={body}
            noSort
            hideButtons
            count={count}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista File/Directories"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </>
  );
};

GestioneBatchDownloadFile.displayName = 'Gestione-batch download file component';
export default GestioneBatchDownloadFile;
